import { h, toast } from './dom';
import { previewUrl, resolveApiUrl, type Friend, type Level, type Photo, type User } from '../api/client';

const COLORS = ['#f7a8b8', '#a8d8f0', '#f9d38c', '#b8e0c2', '#c9b6ef', '#f5b38f', '#9fd3c7'];

/** Round avatar: picture if available, else coloured initials. */
export function avatar(u: Pick<User, 'id' | 'displayName' | 'avatarUrl'> | null | undefined, size = 40) {
  const name = u?.displayName ?? '?';
  const initials = name.split(/\s+/).filter(Boolean).slice(-2).map((w) => w[0]).join('').toUpperCase();
  let hash = 0;
  for (const c of u?.id ?? name) hash = (hash * 31 + c.charCodeAt(0)) >>> 0;
  const el = h('span.avatar', { style: { width: `${size}px`, height: `${size}px`, fontSize: `${size * 0.38}px`, background: COLORS[hash % COLORS.length] } },
    initials || '?');
  const src = resolveApiUrl(u?.avatarUrl);
  if (src) {
    const img = h('img', { src, alt: '', referrerPolicy: 'no-referrer', loading: 'lazy', decoding: 'async' });
    img.onerror = () => img.remove();
    el.append(img);
  }
  return el;
}

/** Overlapping avatars (collab posts, sessions). */
export function avatarStack(users: Pick<User, 'id' | 'displayName' | 'avatarUrl'>[], size = 28, max = 4) {
  const shown = users.slice(0, max);
  return h('span.avstack', { 'aria-hidden': 'true' },
    ...shown.map((u) => avatar(u, size)),
    users.length > max ? h('span.avmore', { style: { width: `${size}px`, height: `${size}px` } }, `+${users.length - max}`) : null);
}

/** 🔥N streak badge (nothing when 0). */
export function streakBadge(days: number | null | undefined, cls = '') {
  if (!days || days < 1) return null;
  return h('span.streak', { class: cls, title: `Chuỗi ${days} ngày`, 'aria-label': `Chuỗi ${days} ngày` }, '🔥', h('b', {}, String(days)));
}

/** Grey placeholder blocks while content loads. */
export function skeleton(kind: 'post' | 'row' | 'grid' = 'post', n = 2) {
  const line = (w: string) => h('i.sk.sk-line', { class: w });
  const one = () => kind === 'post'
    ? h('div.sk-post', {}, h('div.sk-head', {}, h('i.sk.sk-circle'), h('div.sk-lines', {}, line('w60'), line('w30'))), h('i.sk.sk-photo'))
    : kind === 'row'
      ? h('div.sk-row', {}, h('i.sk.sk-circle'), h('div.sk-lines', {}, line('w60'), line('w30')))
      : h('i.sk.sk-tile');
  return h('div.skeleton', { class: `sk-${kind}s`, 'aria-busy': 'true', 'aria-label': 'Đang tải' }, ...Array.from({ length: n }, one));
}

export const LEVELS: Record<Level, { icon: string; name: string }> = {
  NEW: { icon: '🌱', name: 'Mới quen' },
  BLOOM: { icon: '🌸', name: 'Thân' },
  CLOSE: { icon: '💖', name: 'Rất thân' },
  BESTIE: { icon: '💎', name: 'Tri kỷ' },
};

export const levelBadge = (l: Level) => h('span.level', { title: LEVELS[l].name, class: l.toLowerCase() }, LEVELS[l].icon, ' ', LEVELS[l].name);

export function timeAgo(iso: string) {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return 'vừa xong';
  if (s < 3600) return `${Math.floor(s / 60)} phút`;
  if (s < 86400) return `${Math.floor(s / 3600)} giờ`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)} ngày`;
  return new Date(iso).toLocaleDateString('vi-VN');
}

export function empty(icon: string, text: string, action?: HTMLElement | null, title?: string) {
  return h('div.empty', {}, h('div.eicon', { 'aria-hidden': 'true' }, icon), title ? h('h3.etitle', {}, title) : null,
    h('p', {}, text), action);
}

export const spinner = () => h('div.center', {}, h('div.spinner'));

/** Async section: shows a spinner, then the content (or an error with retry). */
export function section<T>(load: () => Promise<T>, view: (data: T, reload: () => void) => HTMLElement | HTMLElement[],
  loading: () => HTMLElement = spinner) {
  const box = h('div.section');
  const run = () => {
    box.replaceChildren(loading());
    load().then((d) => {
      const v = view(d, run);
      box.replaceChildren(...(Array.isArray(v) ? v : [v]));
    }).catch((e) => {
      // 404 = gone or no longer shared with me (e.g. a friend's post after its 24 h): nothing to retry
      if ((e as { status?: number }).status === 404) {
        box.replaceChildren(h('div.error', {}, h('p', {}, 'Nội dung này không còn được chia sẻ với bạn hoặc đã bị xoá.'),
          h('p.tiny', {}, 'Bài đăng của bạn bè chỉ hiện trong 24 giờ.')));
        return;
      }
      box.replaceChildren(h('div.error', {}, h('p', {}, (e as Error).message || 'Có lỗi xảy ra'),
        h('button.secondary', { onclick: run }, 'Thử lại')));
    });
  };
  run();
  return box;
}

/**
 * Photo from the server. Uses the feed's presigned WebP thumb when present (no extra
 * API round trip), else the presigned PREVIEW render. Space is reserved from the
 * photo's width/height so nothing jumps when it arrives; decoding happens off-thread.
 */
export function photoImg(p: Photo, cls = 'photo', o: { eager?: boolean } = {}) {
  const img = h('img', { class: cls, alt: '', loading: o.eager ? 'eager' : 'lazy', decoding: 'async' });
  if (o.eager) img.setAttribute('fetchpriority', 'high');
  if (p.width && p.height) img.style.aspectRatio = `${p.width} / ${p.height}`;
  const viaApi = () => void previewUrl(p).then((u) => (img.src = u)).catch(() => img.classList.add('broken'));
  const thumb = resolveApiUrl(p.thumbUrl);
  if (thumb) {
    img.addEventListener('error', viaApi, { once: true }); // expired link (e.g. cached feed) → API
    img.src = thumb;
  } else viaApi();
  return img;
}

/** Bottom sheet modal. Returns a close function. */
export function sheet(title: string, content: HTMLElement, opts: { onClose?: () => void } = {}) {
  const prevFocus = document.activeElement as HTMLElement | null;
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); close(); } };
  const close = () => {
    if (!overlay.isConnected) return;
    overlay.remove();
    removeEventListener('keydown', onKey, true);
    prevFocus?.focus?.();
    opts.onClose?.();
  };
  const closeBtn = h('button.icon', { title: 'Đóng', 'aria-label': 'Đóng', onclick: close }, '✕');
  const overlay = h('div.sheet-overlay', {},
    h('div.bsheet', { role: 'dialog', 'aria-modal': 'true', 'aria-label': title },
      h('span.grabber', { 'aria-hidden': 'true' }),
      h('header.vhead', {}, h('h2', {}, title), closeBtn),
      content));
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  addEventListener('keydown', onKey, true);
  document.body.append(overlay);
  setTimeout(() => closeBtn.focus({ preventScroll: true }), 30);
  return close;
}

export function confirmBox(text: string, ok = 'Đồng ý'): Promise<boolean> {
  return new Promise((resolve) => {
    let done = false;
    const finish = (v: boolean) => { if (!done) { done = true; resolve(v); close(); } };
    const close = sheet('Xác nhận', h('div.confirm', {}, h('p', {}, text),
      h('div.row', {},
        h('button.secondary', { onclick: () => finish(false) }, 'Huỷ'),
        h('button.primary', { onclick: () => finish(true) }, ok))), { onClose: () => finish(false) });
  });
}

/** Pick friends (sorted by closeness – the API already returns them that way). */
export function pickFriends(friends: Friend[], title: string, action: string, exclude: string[] = []): Promise<string[]> {
  return new Promise((resolve) => {
    const chosen = new Set<string>();
    let done = false;
    const list = friends.filter((f) => !exclude.includes(f.user.id));
    const btn = h('button.primary', { disabled: true }, action);
    const content = h('div.picker', {},
      list.length
        ? h('div.plist', {}, ...list.map((f) => {
          const row = h('label.prow', {},
            h('input', { type: 'checkbox', onchange: (e: Event) => {
              const on = (e.target as HTMLInputElement).checked;
              if (on) chosen.add(f.user.id); else chosen.delete(f.user.id);
              btn.disabled = chosen.size === 0;
              btn.textContent = chosen.size ? `${action} (${chosen.size})` : action;
            } }),
            avatar(f.user, 36), h('span.pname', {}, f.user.displayName), levelBadge(f.level));
          return row;
        }))
        : empty('🫂', 'Chưa có bạn bè nào để mời. Kết bạn ở tab Bạn bè nhé.'),
      btn);
    const close = sheet(title, content, { onClose: () => { if (!done) { done = true; resolve([]); } } });
    btn.onclick = () => { done = true; resolve([...chosen]); close(); };
  });
}

export async function guard<T>(p: Promise<T>, okMsg?: string): Promise<T | undefined> {
  try {
    const v = await p;
    if (okMsg) toast(okMsg);
    return v;
  } catch (e) {
    toast((e as Error).message || 'Có lỗi xảy ra', 3500);
    return undefined;
  }
}
